import React, { useState, useEffect, useRef } from "react";
import Theads from "./Element/Theads";
import Tbodys from "./Element/Tbodys";
import "./tables.css";
import { useDispatch, useSelector } from "react-redux";
import {
  setSearchFields,
  setCurrentPage,
  setSelectedCard,
  getOperationFilteredDataInit,
  getOperationDataInit,
} from "../../../redux/slices/operationHomeSlice";
import getIntervalParams from "../../Helper/getIntervalParams";
import AudioPlayerFile from "../../../operations/components/AudioPlayer/AudioPlayer";
import Modal from "../Modal/Modal";
import { formatDateForFilter } from "../../Helper/formatDate";
const Tables = ({
  data,
  columnConfig,
  handleSearchTransactionTable,
  handleClearAllFilters,
  handleSort,
  transactionTable,
}) => {
  const dispatch = useDispatch();

  const searchFields = useSelector((state) => state.operationHome.searchFields);
  const pageSize = useSelector((state) => state.operationHome.table.pageSize);
  const activeTab = useSelector((state) => state.operationHome.activeTab);
  const startDate = useSelector((state) => state.operationHome.tabStartDate);
  const endDate = useSelector((state) => state.operationHome.tabEndDate);

  const [audioData, setAudioData] = useState(null);
  const [isModalVisible, setModalVisible] = useState(false);

  const [activeSearches, setActiveSearches] = useState({});
  const tableContainerRef = useRef(null);

  /*
  ------------------------------------------------
  Restore filters from Redux when component mounts
  ------------------------------------------------
  */

  useEffect(() => {
    if (searchFields?.searches) {
      const restored = {};

      searchFields.searches.forEach(({ search, searchText }) => {
        restored[search] = searchText;
      });

      setActiveSearches(restored);
    } else {
      setActiveSearches({});
    }
  }, [searchFields]);

  /*
  -----------------------
  Handle Column Searching
  -----------------------
  */

  const handleSearch = (columnId, searchTerm) => {
    const columnMapping = {
      memberId: "customerId",
      category: "callCategory",
      status: "callStatus",
      CRSScore: "crsTotalScoreOfFive",
    };

    const searchColumn = columnMapping[columnId] || columnId;

    let updatedSearch = { ...activeSearches };

    if (!searchTerm || searchTerm.trim() === "") {
      delete updatedSearch[searchColumn];
    } else {
      updatedSearch[searchColumn] = searchTerm.trim();
    }

    setActiveSearches(updatedSearch);

    // All searches for Redux state and API (including qualityOfCall for backend range filtering)
    const allSearches = Object.entries(updatedSearch).map(([key, value]) => ({
      search: key,
      searchText: value,
    }));

    const apiSearches = allSearches;

    console.log(
      `[Tables] handleSearch → columnId: "${columnId}", searchColumn: "${searchColumn}", searchTerm: "${searchTerm}"`,
    );
    console.log(
      "[Tables] searches array being sent to API:",
      JSON.stringify(apiSearches, null, 2),
    );

    dispatch(
      setSearchFields({
        searches: allSearches,
        page: 1,
      }),
    );

    dispatch(setCurrentPage(1));
    dispatch(setSelectedCard(null));

    const params = getIntervalParams(activeTab, startDate, endDate);
    params.limit = pageSize;

    if (apiSearches.length === 0) {
      dispatch(
        getOperationDataInit({
          name: activeTab,
          startDate,
          endDate,
          limit: pageSize,
        }),
      );
    } else {
      dispatch(
        getOperationFilteredDataInit({
          body: { searches: apiSearches, page: 1 },
          allSearches,
          params,
        }),
      );
    }
  };

  /*
  --------------------------------------------------------
  Unified search — always updates activeSearches for tags,
  then delegates to the right handler
  --------------------------------------------------------
  */

  const handleSearchUnified = (columnId, searchTerm) => {
    // "All" is treated as clearing the filter for that column
    const effectiveTerm = searchTerm === "All" ? "" : searchTerm;

    const updatedSearch = { ...activeSearches };
    if (!effectiveTerm || effectiveTerm.trim() === "") {
      delete updatedSearch[columnId];
    } else {
      updatedSearch[columnId] = effectiveTerm.trim();
    }
    setActiveSearches(updatedSearch);

    if (transactionTable) {
      handleSearchTransactionTable(columnId, effectiveTerm);
    } else {
      handleSearch(columnId, effectiveTerm);
    }
  };

  /*
  -------------------
  Clear All Filters
  -------------------
  */

  const clearAllFilters = () => {
    setActiveSearches({});

    if (transactionTable && handleClearAllFilters) {
      handleClearAllFilters();
      return;
    }

    dispatch(
      setSearchFields({
        searches: [],
        page: 1,
      }),
    );

    dispatch(
      getOperationDataInit({
        name: activeTab,
        startDate,
        endDate,
        limit: pageSize,
      }),
    );
  };

  /*
  -------------------
  Audio Player Logic
  -------------------
  */

  const handleAudioClick = async (audioUrl, memberName, role, dateCreated) => {
    const authToken = localStorage.getItem("authToken");

    setAudioData({
      audioSrc: `${process.env.REACT_APP_GET_OPERATION_HOME_API}&secureId=${audioUrl}&token=${authToken}`,
      name: memberName,
      role,
      dateCreated,
    });

    setModalVisible(true);
  };

  const handleCloseAudioPlayer = () => {
    setAudioData(null);
    setModalVisible(false);
  };

  /*
  -------------------
  Active Filter UI
  -------------------
  */

  const renderActiveFilters = () => {
    if (Object.keys(activeSearches).length === 0) return null;

    return (
      <div className="active-filters-container">
        <div className="active-filters-content">
          <span className="active-filters-label">Active Filters:</span>

          <div className="active-filters-tags">
            {Object.entries(activeSearches).map(([column, term]) => (
              <div key={column} className="filter-tag">
                <span className="filter-column">
                  {getColumnDisplayName(column) === "Member ID"
                    ? "Magic Health ID"
                    : getColumnDisplayName(column)}
                  :
                </span>

                <span className="filter-term">
                  {getColumnDisplayName(column) === "Date"
                    ? formatDateForFilter(term)
                    : (columnConfig.find((c) => c.id === column)
                        ?.dropdownLabels?.[term] ?? term)}
                </span>

                <button
                  className="remove-filter-btn"
                  onClick={() => handleSearchUnified(column, "")}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <button className="clear-all-filters-btn" onClick={clearAllFilters}>
            Clear All
          </button>
        </div>
      </div>
    );
  };

  /*
  -------------------
  Column Display Names
  -------------------
  */

  const getColumnDisplayName = (columnKey) => {
    const displayNames = {
      customerId: "Member ID",
      memberName: "Member Name",
      agentName: "Agent Name",
      urgency: "Urgency",
      CallType: "Call Type",
      qualityOfCall: "Quality",
      crsTotalScoreOfFive: "CRS Score",
      MobileNumber: "Mobile Number",
      ReferralType: "Referral Type",
      HID: "HID",
      MemberName: "Member Name",
      // agentScore: "Agent Score",
      AgentName: "Agent Name",
      callCategory: "Category",
      callStatus: "Status",
      engagementDate: "Date",
      severity: "Severity",
    };

    return displayNames[columnKey] || columnKey;
  };

  // Scroll table to top whenever data changes (e.g. after filtering)
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [data]);

  return (
    <>
      {renderActiveFilters()}

      <div className="table-container" ref={tableContainerRef}>
        <table className="responsive-table">
          <colgroup>
            {columnConfig.map((column) => (
              <col key={column.id} style={{ width: column.columnWidth }} />
            ))}
          </colgroup>

          <Theads
            columnConfig={columnConfig}
            data={data}
            onSearch={handleSearchUnified}
            handleSort={handleSort}
            transactionTable={transactionTable}
            activeSearches={activeSearches}
          />

          <Tbodys
            data={data}
            columnConfig={columnConfig}
            onAudioHandler={handleAudioClick}
          />
        </table>

        <Modal show={isModalVisible} modelClosed={handleCloseAudioPlayer}>
          {audioData && (
            <AudioPlayerFile
              name={audioData.name}
              role={audioData.role}
              audioSrc={audioData.audioSrc}
              dateCreated={audioData.dateCreated}
              handleCloseAudioPlayer={handleCloseAudioPlayer}
              mode="audio"
            />
          )}
        </Modal>
      </div>
    </>
  );
};

export default Tables;
